import axios from 'axios';
import { authHeader } from './auth-header';

// Nothing attaches the bearer token automatically in this app — there is no
// axios request interceptor — so every call below spreads authHeader() itself.
export const adjudicationService = {
    getTickets,
    getSummary,
    getTicket,
    setStatus,
    sendMessage,
    uploadDocuments,
    setHearing,
    setAppealStatus,
    resolve,
    getLiftReasons,
    amendAmount,
    voidRecord
};

function getTickets({
    page,
    pageSize,
    search,
    sortColumn,
    orderBy,
    status,
    lgu_id,
    mine,
    include_closed
}) {
    return axios.get('/adjudication/tickets', {
        params: {
            page,
            pageSize,
            search,
            sortColumn,
            orderBy,
            status,
            lgu_id,
            mine,
            include_closed
        },
        headers: authHeader()
    })
    .then(({ data }) => data);
}

function getSummary() {
    return axios.get('/adjudication/summary', { headers: authHeader() })
        .then(({ data }) => data);
}

function getTicket(id) {
    return axios.get(`/adjudication/tickets/${id}`, { headers: authHeader() })
        .then(({ data }) => data);
}

function setStatus(id, status) {
    return axios.post(`/adjudication/tickets/${id}/status`, { status }, { headers: authHeader() })
        .then(({ data }) => data);
}

/**
 * A message, optionally with the documents it is about.
 *
 * Always multipart, even with no files: one code path rather than a JSON body
 * that silently becomes multipart the moment someone attaches something.
 */
function sendMessage(id, { body, request_documents, files }) {
    const form = new FormData();
    form.append('body', body);
    form.append('request_documents', request_documents ? 1 : 0);
    (files || []).forEach((file) => form.append('files[]', file));

    return axios.post(`/adjudication/tickets/${id}/message`, form, {
        headers: { ...authHeader(), 'Content-Type': 'multipart/form-data' }
    }).then(({ data }) => data);
}

function uploadDocuments(id, files) {
    const form = new FormData();
    files.forEach((file) => form.append('files[]', file));

    return axios.post(`/adjudication/tickets/${id}/documents`, form, {
        headers: { ...authHeader(), 'Content-Type': 'multipart/form-data' }
    }).then(({ data }) => data);
}

function setHearing(id, { hearing_at, hearing_venue, hearing_remarks, cancel_remarks }) {
    return axios.post(
        `/adjudication/tickets/${id}/hearing`,
        { hearing_at, hearing_venue, hearing_remarks, cancel_remarks },
        { headers: authHeader() }
    ).then(({ data }) => data);
}

/**
 * Decide one contested NOV. Final once set — the server refuses a second call.
 */
function setAppealStatus(id, appealId, { status, notes }) {
    return axios.post(
        `/adjudication/tickets/${id}/appeals/${appealId}/status`,
        { status, notes },
        { headers: authHeader() }
    ).then(({ data }) => data);
}

function resolve(id, { resolution, notes }) {
    return axios.post(
        `/adjudication/tickets/${id}/resolve`,
        { resolution, notes },
        { headers: authHeader() }
    ).then(({ data }) => data);
}

function getLiftReasons() {
    return axios.get('/lift_reasons', { headers: authHeader() })
        .then(({ data }) => data);
}

/**
 * Amend the penalty amount through the EXISTING endpoint rather than an
 * adjudication-specific one — spec 3 drives the audited action, it does not
 * reimplement it.
 *
 * The attachment is not optional even though the endpoint's validator is
 * commented out: ViolationPenaltyController@changeAmount does all of its work
 * inside `foreach ($attachments as $attachment)`, so with no file the loop never
 * runs and the method dies dereferencing an unset variable.
 */
function amendAmount({ nov_number, amount, attachment }) {
    const form = new FormData();
    form.append('nov_number', nov_number);
    form.append('amount', amount);
    form.append('attachment[]', attachment);

    return axios.post('/traffic_record_component/violation_penalty/changeAmount', form, {
        headers: { ...authHeader(), 'Content-Type': 'multipart/form-data' }
    }).then(({ data }) => data);
}

/** The existing void action. All four fields are required by its form request. */
function voidRecord({ traffic_record_id, note, lift_reason_id, attachment }) {
    const form = new FormData();
    form.append('traffic_record_id', traffic_record_id);
    form.append('note', note);
    form.append('lift_reason_id', lift_reason_id);
    form.append('attachment', attachment);

    return axios.post('/void', form, {
        headers: { ...authHeader(), 'Content-Type': 'multipart/form-data' }
    }).then(({ data }) => data);
}
